import { useMemo, useState } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { PolicyTemplateView, StorePolicyType, StorePolicyView } from "@/lib/api/types";
import { useUpdateStoreMutation } from "@/modules/settings/services/useUpdateStoreMutation";
import { usePolicyTemplatesQuery } from "@/modules/settings/services/usePolicyTemplatesQuery";
import { Alert, Button, ToggleInput, SuccessNotice } from "@a2z/ui";
import { PolicyEditSheet } from "./PolicyEditSheet";

type PolicyItem = {
  policyType: StorePolicyType;
  isEnabled: boolean;
  contentAr: string;
  contentEn: string;
};

function buildItemsMap(policies: StorePolicyView[]): Map<StorePolicyType, PolicyItem> {
  const map = new Map<StorePolicyType, PolicyItem>();
  for (const p of policies) {
    map.set(p.policyType, {
      policyType: p.policyType,
      isEnabled: p.isEnabled,
      contentAr: p.contentAr,
      contentEn: p.contentEn,
    });
  }
  return map;
}

export function PoliciesForm({
  storeSlug,
  initialPolicies,
}: {
  storeSlug: string;
  initialPolicies: StorePolicyView[];
}) {
  const t = useTranslations();
  const locale = useLocale();

  const { data: templates, isLoading: templatesLoading } = usePolicyTemplatesQuery({
    storeSlug,
  });

  // Local working copy keyed by policyType
  const [itemsMap, setItemsMap] = useState<Map<StorePolicyType, PolicyItem>>(
    () => buildItemsMap(initialPolicies),
  );

  // Sync from server after mutations invalidate the store query —
  // render-time previous-value comparison instead of a setState-in-effect.
  const [prevInitialPolicies, setPrevInitialPolicies] = useState(initialPolicies);
  if (initialPolicies !== prevInitialPolicies) {
    setPrevInitialPolicies(initialPolicies);
    setItemsMap(buildItemsMap(initialPolicies));
  }

  // Feedback for inline toggle saves
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [toggleSuccessKey, setToggleSuccessKey] = useState<string | null>(null);

  // Edit modal state
  const [editingType, setEditingType] = useState<StorePolicyType | null>(null);
  const [editPrefill, setEditPrefill] = useState<{ contentAr: string; contentEn: string }>({
    contentAr: "",
    contentEn: "",
  });
  const [isPrefilledWithTemplate, setIsPrefilledWithTemplate] = useState(false);

  const { mutateAsync: updateStore } = useUpdateStoreMutation();

  const getItem = (policyType: StorePolicyType): PolicyItem =>
    itemsMap.get(policyType) ?? {
      policyType,
      isEnabled: false,
      contentAr: "",
      contentEn: "",
    };

  const getTemplateLabel = (template: PolicyTemplateView) =>
    locale === "en" ? (template.titleEn ?? template.titleAr) : template.titleAr;

  /**
   * Builds the full payload for updateStore, applying an optional override to
   * one policy type. Uses template order for sortOrder when templates are loaded.
   */
  const buildPayload = useMemo(
    () =>
      (overrideType?: StorePolicyType, override?: Partial<PolicyItem>) => {
        const displayOrder: { policyType: StorePolicyType }[] =
          templates ?? initialPolicies.map((p) => ({ policyType: p.policyType }));
        return displayOrder.map((entry) => {
          const item = itemsMap.get(entry.policyType) ?? {
            policyType: entry.policyType,
            isEnabled: false,
            contentAr: "",
            contentEn: "",
          };
          const patched =
            overrideType === entry.policyType ? { ...item, ...override } : item;
          return {
            policyType: patched.policyType,
            isEnabled: patched.isEnabled,
            contentAr: patched.contentAr.trim(),
            contentEn: patched.contentEn.trim(),
          };
        });
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates, initialPolicies, itemsMap],
  );

  /** Toggle is saved immediately with an optimistic update + rollback on error. */
  const handleToggle = async (policyType: StorePolicyType, isEnabled: boolean) => {
    setToggleError(null);
    setToggleSuccessKey(null);

    // Snapshot for rollback
    const snapshot = itemsMap.get(policyType);
    const payload = buildPayload(policyType, { isEnabled });

    // Optimistic update
    setItemsMap((prev) => {
      const next = new Map(prev);
      next.set(policyType, { ...getItem(policyType), isEnabled });
      return next;
    });

    try {
      await updateStore({ storeSlug, policies: payload });
      setToggleSuccessKey(`toggle-${Date.now()}`);
    } catch (err) {
      // Rollback
      setItemsMap((prev) => {
        const next = new Map(prev);
        if (snapshot) next.set(policyType, snapshot);
        else next.delete(policyType);
        return next;
      });
      setToggleError(
        err instanceof Error ? err.message : t(keys.errors.generic),
      );
    }
  };

  const openEdit = (
    policyType: StorePolicyType,
    contentAr: string,
    contentEn: string,
    isTemplate = false,
  ) => {
    setEditingType(policyType);
    setEditPrefill({ contentAr, contentEn });
    setIsPrefilledWithTemplate(isTemplate);
  };

  /**
   * Called by PolicyEditSheet on Save. Throws on failure so the sheet stays open.
   */
  const handleSaveEdit = async (contentAr: string, contentEn: string) => {
    if (!editingType) return;
    const payload = buildPayload(editingType, { contentAr, contentEn });
    // Optimistic update
    setItemsMap((prev) => {
      const next = new Map(prev);
      next.set(editingType, { ...getItem(editingType), contentAr, contentEn });
      return next;
    });
    // This throws on failure → PolicyEditSheet catches it and stays open
    await updateStore({ storeSlug, policies: payload });
    setEditingType(null);
  };

  const editingTemplate = templates?.find((tpl) => tpl.policyType === editingType);
  const editingPolicyLabel = editingTemplate ? getTemplateLabel(editingTemplate) : "";

  return (
    <div className="flex flex-col gap-4">
      {toggleError ? (
        <Alert message={toggleError} helperText={t(keys.errors.recovery)} />
      ) : null}
      {toggleSuccessKey ? (
        <SuccessNotice
          key={toggleSuccessKey}
          title={t(keys.seller.storePoliciesSavedNotice)}
        />
      ) : null}

      <p className="text-sm text-[color:var(--text-muted)]">
        {t(keys.seller.storePoliciesHelper)}
      </p>

      {/* ── Policy cards ─────────────────────────────────────────────── */}
      {templatesLoading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl bg-[color:var(--surface-alt)]"
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {(templates ?? []).map((template) => {
            const item = getItem(template.policyType);
            const hasContent =
              item.contentAr.trim().length > 0 || item.contentEn.trim().length > 0;
            const policyLabel = getTemplateLabel(template);
            const templateHasContent =
              template.contentAr.trim().length > 0 ||
              (template.contentEn ?? "").trim().length > 0;

            return (
              <div
                key={template.policyType}
                className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4"
              >
                {/* Card header: title + action buttons + toggle */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-semibold text-[color:var(--text-primary)]">
                    {policyLabel}
                  </span>

                  <div className="flex shrink-0 items-center gap-2">
                    {/* "Use template" appears only when there is no store content yet */}
                    {!hasContent && templateHasContent ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        shape="soft"
                        onClick={() =>
                          openEdit(
                            template.policyType,
                            template.contentAr,
                            template.contentEn ?? "",
                            true,
                          )
                        }
                      >
                        {t(keys.seller.storePoliciesUseTemplate)}
                      </Button>
                    ) : null}

                    {/* Edit button — always shown */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      shape="soft"
                      onClick={() =>
                        openEdit(
                          template.policyType,
                          item.contentAr,
                          item.contentEn,
                          false,
                        )
                      }
                    >
                      {t(keys.common.edit)}
                    </Button>

                    {/* Active toggle */}
                    <div className="flex cursor-pointer items-center">
                      <ToggleInput
                        name={`${template.policyType}-enabled`}
                        checked={item.isEnabled}
                        onChange={(e) =>
                          void handleToggle(template.policyType, e.target.checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Content preview or no-content hint */}
                {hasContent ? (
                  <p className="mt-2 line-clamp-2 text-xs text-[color:var(--text-muted)]">
                    {item.contentAr || item.contentEn}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-[color:var(--text-muted)]">
                    {t(keys.seller.storePoliciesNoContent)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit modal ───────────────────────────────────────────────── */}
      <PolicyEditSheet
        isOpen={editingType !== null}
        onClose={() => setEditingType(null)}
        policyLabel={editingPolicyLabel}
        initialContentAr={editPrefill.contentAr}
        initialContentEn={editPrefill.contentEn}
        isPrefilledWithTemplate={isPrefilledWithTemplate}
        onSave={handleSaveEdit}
      />
    </div>
  );
}
